import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, Button } from '@etherisc/ui-kit';
import { FileTextIcon } from 'lucide-react';
import type { Quote, CreateQuoteRequest, UpdateQuoteRequest } from '../../types/quote';
import type { Farmer } from '../../types/farmer';
import { SimpleWizardShell } from '../../framework/wizard/components/SimpleWizardShell';
import { createWizardConfig, createStep, WizardData } from '../../framework/wizard';
import { HybridPersistenceAdapter } from '../../framework/wizard/adapters/HybridPersistenceAdapter';
import { QuoteDatabaseAdapter } from './adapters/QuoteDatabaseAdapter';
import { getInitialWizardData, convertWizardDataToQuote } from './utils/quoteDataConverter';
import { quoteService } from '../../services/quoteService';

// Step components
import { ProductSelectionStepNew } from './steps/ProductSelectionStepNew';
import { ParticipantsStepNew } from './steps/ParticipantsStepNew';
import { LocationVerificationStepNew } from './steps/LocationVerificationStepNew';
import { CoverageDetailsStepNew } from './steps/CoverageDetailsStepNew';
import { ProtectionLevelStepNew } from './steps/ProtectionLevelStepNew';
import { PaymentDetailsStepNew } from './steps/PaymentDetailsStepNew';
import { SummaryApprovalStepNew } from './steps/SummaryApprovalStepNew';

// Types for wizard data
export interface QuoteParticipant {
  farmerId: string;
  farmer?: Farmer;
  role: 'POLICYHOLDER' | 'INSURED';
  isActive: boolean;
  sumInsured?: number; // Sum insured for this participant
  hectaresAnimalCount?: number; // Hectares/Animals Insured for this participant
}

export interface QuoteWizardData extends WizardData {
  // Framework required fields (inherited from WizardData)
  // id?: string;
  // createdAt?: Date;
  // updatedAt?: Date;
  
  // Wizard navigation
  currentWizardStep?: number;
  
  // Proof of insurable interest (Step 2 - Participants)
  proofOfInterestConfirmed?: boolean;
  proofOfInterestDocuments?: Array<{id: string; filename: string; url: string; size?: number; mimeType?: string}>;
  
  // Quote-specific fields
  quote?: Quote;
  
  // Note: Removed templateName - now in quote object only
  
  // Additional fields for future steps
  bulkUploadData?: {
    uploadId?: string;
    fileName?: string;
    status?: string;
    validRecords?: number;
    invalidRecords?: number;
  };
  // Note: Removed locationValidation - now in quote object only
  // Note: Removed protectionLevels, quotationJobId, quotationResults, selectedScenarios, notes, strikeLevel, paymentDetails - now in quote object only
  pricingCalculation?: {
    totalPremium: number;
    breakdown: {
      anrp: number;
      loadings: number;
      fees: number;
      taxes: number;
      subsidies: number;
    };
  };
  recalcNeeded?: boolean;
  
  // Step validation state for navigation (simple array approach)
  validatedSteps?: string[];
  
  // Note: Removed currentWizardStep - wizard framework handles step tracking
}

interface QuoteWizardProps {
  mode?: 'create' | 'edit';
  existingQuote?: Quote;
  onComplete: (quote: Quote) => void;
  onCancel: () => void;
}

// Create wizard configuration using the hybrid persistence approach
const createQuoteWizardConfig = (initialData: QuoteWizardData, mode: 'create' | 'edit' = 'create', enableDatabaseSync = false) => {
  // Always-on localStorage + optional database sync
  const databaseAdapter = enableDatabaseSync ? new QuoteDatabaseAdapter() : undefined;
  const hybridAdapter = new HybridPersistenceAdapter<QuoteWizardData>(
    'quote-wizard-draft',
    databaseAdapter,
    {
      enableBackgroundSync: enableDatabaseSync,
      syncIntervalMs: 60000, // Sync every minute
    }
  );

  return createWizardConfig<QuoteWizardData>('quote-wizard', mode === 'create' ? 'Create New Quote' : 'Edit Quote')
    .description(mode === 'create' ? 'Follow the steps below to create your insurance quote' : 'Modify the quote configuration below')
    .initialData(initialData)
    .persistenceAdapter(hybridAdapter) // Use the hybrid adapter
    .persistence({
      autoSave: { 
        enabled: true, 
        intervalMs: 30000, // Always 30s for localStorage
        onlyWhenDirty: true,
        saveOnStepChange: true 
      },
      draftKeyPrefix: 'quote-wizard',
      saveToDB: enableDatabaseSync,
      saveToLocalStorage: true, // Always enabled
    })
    .addStep(createStep({
    id: 'product',
    title: 'Product & Coverage Selection',
    description: 'Choose product, underlying activities, and coverage items',
      component: ProductSelectionStepNew,
    validation: (data) => {
      const errors: string[] = [];
      // Check product in quote object (single source of truth)
      if (!data.quote?.product && !data.quote?.productId) errors.push('Please select a product');
      // Check coverage config in quote object (single source of truth)
      const underlyingActivities = data.quote?.underlying_assets?.activities_products?.map((a: { type: string }) => a.type) || [];
      const coverageItems = data.quote?.coverageItems?.filter(c => c.selected).map(c => c.type) || [];
      
      if (!underlyingActivities.length) errors.push('Please select at least one underlying activity/product');
      if (!coverageItems.length) errors.push('Please select at least one coverage item');
        
        return { isValid: errors.length === 0, errors };
    }
    }))
    .addStep(createStep({
    id: 'participants',
      title: 'Participants',
      description: 'Select policy type and participants',
      component: ParticipantsStepNew,
    validation: (data) => {
      const errors: string[] = [];
      
      // Check policy type in quote object (single source of truth)
      if (data.quote?.isMasterPolicy === undefined) {
        errors.push('Please select policy type');
      }
      
      const policyType = data.quote?.isMasterPolicy ? 'master' : 'individual';
      if (policyType === 'individual') {
        // Check for policyholder in quote.participants (single source of truth)
        const policyholder = data.quote?.participants?.find(p => p.role === 'POLICYHOLDER' && p.farmerId);
        if (!policyholder) {
          errors.push('Please select a policyholder for individual policy');
        }
        // Sum insured validation for individual policies
        const policyholderParticipant = data.quote?.participants?.find(p => p.role === 'POLICYHOLDER');
        const policyholderSumInsured = policyholderParticipant?.sumInsured;
        
        if (policyholderParticipant && (policyholderSumInsured === undefined || policyholderSumInsured <= 0)) {
          errors.push('Sum insured must be greater than 0 for individual policies');
        }
        
        // Validate sum insured against product limits
        if (policyholderParticipant && policyholderSumInsured !== undefined && policyholderSumInsured > 0) {
          const config = data.quote?.product?.productConfig as unknown as Record<string, unknown>;
          const actualConfig = (config?.data || config) as Record<string, unknown> | undefined;
          const coverageConfig = actualConfig?.coverageConfig as Record<string, unknown> | undefined;
          const sumInsuredLimits = coverageConfig?.sumInsuredLimits as { min?: number; max?: number } | undefined;
          
          if (sumInsuredLimits) {
            if (sumInsuredLimits.min !== undefined && policyholderSumInsured < sumInsuredLimits.min) {
              errors.push(`Sum insured must be at least $${sumInsuredLimits.min.toLocaleString()}`);
            }
            if (sumInsuredLimits.max !== undefined && policyholderSumInsured > sumInsuredLimits.max) {
              errors.push(`Sum insured must not exceed $${sumInsuredLimits.max.toLocaleString()}`);
            }
          }
        }
      } else if (policyType === 'master') {
        // Check for organization in quote.participants (single source of truth)
        const organizationParticipant = data.quote?.participants?.find(p => p.role === 'POLICYHOLDER' && p.organizationId);
        if (!organizationParticipant) {
          errors.push('Please select an organization for master policy');
        }
        
        // #192: Block if zero farmers have been added to master policy
        const insuredParticipants = data.quote?.participants?.filter(p => p.role === 'INSURED') || [];
        if (insuredParticipants.length === 0) {
          errors.push('At least one farmer must be added to proceed');
        }
        
        // Validate sum insured for master policy participants against product limits
        const config = data.quote?.product?.productConfig as unknown as Record<string, unknown>;
        const actualConfig = (config?.data || config) as Record<string, unknown> | undefined;
        const coverageConfig = actualConfig?.coverageConfig as Record<string, unknown> | undefined;
        const sumInsuredLimits = coverageConfig?.sumInsuredLimits as { min?: number; max?: number } | undefined;
        
        if (sumInsuredLimits && insuredParticipants.length > 0) {
          for (const participant of insuredParticipants) {
            const si = participant.sumInsured;
            if (si !== undefined && si > 0) {
              if (sumInsuredLimits.min !== undefined && si < sumInsuredLimits.min) {
                const farmerName = participant.farmer?.name || participant.farmerId || 'Unknown';
                errors.push(`${farmerName}: Sum insured ($${si.toLocaleString()}) is below minimum ($${sumInsuredLimits.min.toLocaleString()})`);
              }
              if (sumInsuredLimits.max !== undefined && si > sumInsuredLimits.max) {
                const farmerName = participant.farmer?.name || participant.farmerId || 'Unknown';
                errors.push(`${farmerName}: Sum insured ($${si.toLocaleString()}) exceeds maximum ($${sumInsuredLimits.max.toLocaleString()})`);
              }
            }
          }
        }
      }
      
      // Require proof of insurable interest confirmation
      if (!data.proofOfInterestConfirmed) {
        errors.push('Please confirm proof of insurable interest before proceeding');
      }
      
        return { isValid: errors.length === 0, errors };
    }
    }))
    .addStep(createStep({
    id: 'location',
    title: 'Location Verification',
    description: 'Verify and confirm insured locations on map',
      component: LocationVerificationStepNew,
    validation: (data) => {
      const errors: string[] = [];
      
      // #138: Block if any farmer lacks coordinates (when product has geographic area restrictions)
      const farmerParticipants = data.quote?.participants?.filter(p => p.farmerId && p.farmer) || [];
      for (const participant of farmerParticipants) {
        if (participant.farmer && (participant.farmer.latitude == null || participant.farmer.longitude == null)) {
          errors.push(`Farmer ${participant.farmer.name || 'Unknown'} has no location coordinates. Please add coordinates before proceeding.`);
        }
      }
      
      // Check location validation in quote object (single source of truth)
      if (!data.quote?.locationValidation?.validation_performed) {
        errors.push('Please verify locations before continuing');
      }
      
      if (data.quote?.locationValidation?.validation_performed && !data.quote?.locationValidation?.all_within_coverage) {
        errors.push('Some locations are outside the product coverage area');
      }
      
        return { isValid: errors.length === 0, errors };
    }
    }))
    .addStep(createStep({
    id: 'coverage',
    title: 'Coverage Period & Perils',
    description: 'Set coverage period, sum insured, and select perils',
      component: CoverageDetailsStepNew,
    validation: (data) => {
      const errors: string[] = [];
      // Check coverage dates in quote object (single source of truth)
      // Note: Removed debug logs - validation working correctly
      
      if (!data.quote?.coverageStartDate) errors.push('Coverage start date is required');
      if (!data.quote?.coverageEndDate) errors.push('Coverage end date is required');
      
      // Check perils selection in quote object (single source of truth)
      const selectedPerils = data.quote?.perils_config?.perils?.map((p: { peril_id: string }) => p.peril_id) || [];
      // Note: Perils validation working correctly
      if (!selectedPerils.length) {
        errors.push('Please select at least one peril');
      }
      
      // Note: Removed debug logs - validation working correctly
      
      // Validate coverage period duration
      if (data.quote?.coverageStartDate && data.quote?.coverageEndDate) {
        const startDate = new Date(data.quote.coverageStartDate);
        const endDate = new Date(data.quote.coverageEndDate);
        const diffInDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        
        // Extract coverage period limits from product config for proper validation
        const config = data.quote?.product?.productConfig as unknown as Record<string, unknown>;
        const actualConfig = config?.data || config;
        const coverageConfig = (actualConfig as Record<string, unknown>)?.coverageConfig as Record<string, unknown>;
        const periodLimits = coverageConfig?.coveragePeriodLimits as Record<string, unknown>;
        
        if (periodLimits) {
          const limitType = periodLimits.type as string;
          
          if (limitType === 'duration_days') {
            // Day range validation
            const minDays = periodLimits.minDurationDays as number || 90;
            const maxDays = periodLimits.maxDurationDays as number || 365;
            
            if (diffInDays < minDays) {
              errors.push(`Coverage period must be at least ${minDays} days`);
            }
            if (diffInDays > maxDays) {
              errors.push(`Coverage period must be less than ${maxDays + 1} days`);
            }
          } else if (limitType === 'date_range') {
            // Date range validation
            const startDateRange = periodLimits.startDateRange as { earliest: string; latest: string };
            const endDateRange = periodLimits.endDateRange as { earliest: string; latest: string };
            
            if (startDateRange) {
              const earliestStart = new Date(startDateRange.earliest);
              const latestStart = new Date(startDateRange.latest);
              
              if (startDate < earliestStart) {
                errors.push(`Start date cannot be earlier than ${startDateRange.earliest}`);
              }
              if (startDate > latestStart) {
                errors.push(`Start date cannot be later than ${startDateRange.latest}`);
              }
            }
            
            if (endDateRange) {
              const earliestEnd = new Date(endDateRange.earliest);
              const latestEnd = new Date(endDateRange.latest);
              
              if (endDate < earliestEnd) {
                errors.push(`End date cannot be earlier than ${endDateRange.earliest}`);
              }
              if (endDate > latestEnd) {
                errors.push(`End date cannot be later than ${endDateRange.latest}`);
              }
            }
          }
        } else {
          // Fallback to default validation if no product limits
          if (diffInDays < 90) {
            errors.push('Coverage period must be at least 90 days');
          }
          if (diffInDays > 364) {
            errors.push('Coverage period must be less than 365 days');
          }
        }
        
        if (startDate >= endDate) {
          errors.push('Coverage end date must be after start date');
        }
        
        // Validate pentad boundaries only if dateValidation is "pentads" (or undefined, which defaults to pentads)
        const dateValidation = periodLimits?.dateValidation as string | undefined;
        if (dateValidation !== 'none') {
          const startDay = startDate.getUTCDate();
          const endDay = endDate.getUTCDate();
          
          // Pentads start on 1st, 6th, 11th, 16th, 21st, 26th of month
          const validStartDays = [1, 6, 11, 16, 21, 26];
          if (!validStartDays.includes(startDay)) {
            errors.push(`Coverage start date must be on pentad boundaries: ${validStartDays.join(', ')}`);
          }
          
          // Pentads end on 5th, 10th, 15th, 20th, 25th, or last day of month
          const lastDayOfMonth = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() + 1, 0)).getUTCDate();
          const validEndDays = [5, 10, 15, 20, 25, lastDayOfMonth];
          if (!validEndDays.includes(endDay)) {
            errors.push(`Coverage end date must be on pentad boundaries: ${validEndDays.join(', ')} or last day of month (${lastDayOfMonth})`);
          }
        }
      }
      
      // Sum insured validation moved to Step 2 for individual policies
      const protectionPolicyType = data.quote?.isMasterPolicy ? 'master' : 'individual';
      if (protectionPolicyType === 'master') {
        // For master policies, sum insured comes from bulk upload
        if (!data.bulkUploadData?.validRecords || data.bulkUploadData.validRecords === 0) {
          errors.push('Bulk upload data is required for master policies');
        }
      } else if (data.quote?.isMasterPolicy === undefined) {
        errors.push('Policy type must be selected');
      }
      
      // Note: Perils validation moved to coverage step to avoid duplicate error messages
      
      // Validate peril weights sum to 100% from quote object (#182: epsilon comparison for floating-point)
      if (data.quote?.perils_config?.perils?.length) {
        const totalWeight = data.quote.perils_config.perils.reduce((sum: number, p: { weight?: number }) => sum + (p.weight || 0), 0);
        if (Math.abs(totalWeight - 100) > 0.1) {
          errors.push('Peril weights must sum to 100%');
        }
      }
      
        return { isValid: errors.length === 0, errors };
    }
    }))
    .addStep(createStep({
    id: 'protection',
    title: 'Protection Level Selection',
      description: 'Choose protection levels and calculate pricing',
      component: React.memo(ProtectionLevelStepNew),
    validation: (data) => {
      const errors: string[] = [];
      
        // Protection levels are automatically set when calculation results are available
        // No manual validation needed since they're selected by default from sliders
        
        // Check if recalculation is needed - block navigation if true
        if (data.recalcNeeded) {
          errors.push('Recalculation needed - please recalculate or continue with current results');
        }
        
        // Validate pricing calculation is complete
        // Check both new structure (quote.quotationResults) and old structure (pricingCalculation) for backward compatibility
        const hasQuotationResults = !!data.quote?.quotationResults;
        const hasPricingCalculation = !!data.pricingCalculation?.totalPremium;
        
        if (!hasQuotationResults && !hasPricingCalculation) {
          errors.push('Please calculate pricing before proceeding');
        }
        
        return { isValid: errors.length === 0, errors };
      }
    }))
    .addStep(createStep({
      id: 'payment',
      title: 'Payment Details',
      description: 'Review and update payment information for insureds',
      component: React.memo(PaymentDetailsStepNew),
      validation: (data) => {
        // Payment details page - no validation needed, just display
        // Allow navigation once protection levels are set (calculation completed)
        return { isValid: !!data.quote?.protectionLevels, errors: [] };
      }
    }))
    .addStep(createStep({
      id: 'review',
      title: 'Summary & Approval',
      description: 'Review quote details and approve',
      component: React.memo(SummaryApprovalStepNew),
      validation: (data) => {
        // Summary & approval page - no validation needed, just display
        // Allow navigation once protection levels are set (calculation completed)
        return { isValid: !!data.quote?.protectionLevels, errors: [] };
      }
    }))
    .onSubmit(async (data) => {
      // Handle both create and edit modes
      try {
        const quoteData = convertWizardDataToQuote(data);
        
        let resultQuote: Quote;
        
        if (mode === 'edit' && data.quote?.id) {
          // Update existing quote
          const updatedQuote = await quoteService.updateQuote(data.quote.id, quoteData as unknown as UpdateQuoteRequest);
          resultQuote = updatedQuote.quote;
          toast.success('Quote updated successfully!');
        } else {
          // Create new quote
          const newQuote = await quoteService.createQuote(quoteData as unknown as CreateQuoteRequest);
          resultQuote = newQuote.quote;
          toast.success('Quote created successfully!');
        }
        
        // Framework expects void, so we store quote in a global for now
        (globalThis as Record<string, unknown>).__completedQuote = resultQuote;
        } catch (error) {
        console.error(`Failed to ${mode} quote:`, error);
        toast.error(`Failed to ${mode} quote. Please try again.`);
        throw error;
      }
    })
    .build();
};

export function QuoteWizard({ mode = 'create', existingQuote, onComplete, onCancel }: QuoteWizardProps) {
  const navigate = useNavigate();
  
  // Clear localStorage when starting a new create session to prevent data inheritance
  React.useEffect(() => {
    if (mode === 'create') {
      // Clear any existing wizard draft data to start fresh
      localStorage.removeItem('quote-wizard-draft');
      console.log('🔄 CREATE MODE: Cleared localStorage to start fresh');
    }
  }, [mode]);
  
  // Get initial data based on mode (create vs edit) - no separate Zustand store needed
  const wizardData = getInitialWizardData(mode, existingQuote);
  
  // Create wizard configuration with hybrid persistence
  // LocalStorage is always enabled, database sync always enabled
  const enableDatabaseSync = true; // Always enable database sync for Save as Draft
  const wizardConfig = createQuoteWizardConfig(wizardData, mode, enableDatabaseSync);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-md"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === 'create' ? 'Create New Quote' : 'Edit Quote'}
              {mode === 'edit' && existingQuote && (
                <span className="text-lg font-normal text-gray-600 ml-2">
                  ({existingQuote.quoteNumber})
                </span>
              )}
            </h1>
            <p className="text-gray-600 mt-1">
              {mode === 'create' 
                ? 'Follow the steps below to create your insurance quote'
                : 'Modify the quote configuration below'
              }
            </p>
          </div>
        </div>
        
        {mode === 'edit' && existingQuote && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/quotes/${existingQuote.id}/documents`)}
            className="flex items-center gap-2"
          >
            <FileTextIcon size={16} />
            Quote Documents
          </Button>
        )}
      </div>

      {/* Wizard Shell handles all navigation, progress, and step rendering */}
      <SimpleWizardShell
        config={wizardConfig}
        initialData={wizardData}
        onSubmitSuccess={() => {
          // Get the completed quote from global storage
          const completedQuote = (globalThis as Record<string, unknown>).__completedQuote as Quote;
          if (completedQuote) {
            onComplete(completedQuote);
            delete (globalThis as Record<string, unknown>).__completedQuote;
          } else {
            toast.error('Quote creation completed but quote data is missing');
          }
        }}
        onSubmitError={(error) => toast.error(error.message)}
      />
    </div>
  );
} 
